import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, shareReplay, tap, throwError } from 'rxjs';

export interface CurrencyInfo {
  symbol: string;
  code: string;
  name?: string;
}

export interface CurrencyFormatOptions {
  showSymbol?: boolean;
  showCode?: boolean;
  precision?: number;
  useThousandsSeparator?: boolean;
  locale?: string;
  /** When true, skip FX conversion and format in the given (or base) currency. */
  skipConversion?: boolean;
  notConversion?: boolean;
  module?: string;
}

interface FrankfurterResponse {
  amount?: number;
  base?: string;
  date?: string;
  rates: Record<string, number>;
}

/** Public FX API — must not receive app auth/tenant headers (CORS preflight would fail). */


@Injectable({
  providedIn: 'root',
})
export class CurrencyService {
  private readonly DEFAULT_CURRENCY: CurrencyInfo = {
    symbol: '₹',
    code: 'INR',
    name: 'Indian Rupee',
  };

  /** Header has no currency picked — display stays in tenant base, no FX. */
  private readonly CLEARED_SELECTION: CurrencyInfo = {
    symbol: '',
    code: '',
    name: '',
  };

  /** Tenant / API default currency — amounts from the backend are in this code unless specified. */
  private baseCurrency: CurrencyInfo = { ...this.DEFAULT_CURRENCY };

  readonly selectedCurrency = signal<CurrencyInfo>({ ...this.DEFAULT_CURRENCY });
  readonly exchangeRate = signal<number>(1);
  readonly isConverting = signal<boolean>(false);
  /** False on load (tenant base); true only after user picks a different header currency. */
  readonly conversionEnabled = signal<boolean>(false);
  /** Bumped on every FX or selection change so templates re-render converted amounts. */
  readonly currencyVersion = signal<number>(0);

  private readonly currencyChangeSubject = new BehaviorSubject<CurrencyInfo>({
    ...this.DEFAULT_CURRENCY,
  });
  readonly currencyChange$ = this.currencyChangeSubject.asObservable();
  FRANKFURTER_API = `https://api.frankfurter.dev/v1/latest`;
  private readonly rateCache = new Map<string, number>();
  private readonly pendingRates = new Map<string, Observable<number>>();
  /** HttpClient without interceptors — avoids Authorization / tenant headers on public APIs. */
  private readonly externalHttp: HttpClient;
  constructor(httpBackend: HttpBackend) {
    this.externalHttp = new HttpClient(httpBackend);
   
  }
 
  /**
   * Set tenant base currency once (e.g. from auth / header on init).
   */
  initializeFromTenant(code?: string, symbol?: string): void {
    if (!code) {
      return;
    }
    const normalized = code.trim().toUpperCase();
    const info: CurrencyInfo = {
      code: normalized,
      symbol: symbol?.trim() || this.getCurrencySymbol(normalized),
      name: normalized,
    };
    this.baseCurrency = { ...info };
    Object.assign(this.DEFAULT_CURRENCY, info);
    this.conversionEnabled.set(false);
    this.selectedCurrency.set({ ...this.CLEARED_SELECTION });
    this.exchangeRate.set(1);
    this.isConverting.set(false);
    this.notifyCurrencyChange();
  }

  /**
   * User chose "Select Currency" — stop header FX only.
   * Tenant base currency is unchanged; amounts re-format via notifyCurrencyChange().
   */
  clearCurrencySelection(): void {
    this.conversionEnabled.set(false);
    this.exchangeRate.set(1);
    this.isConverting.set(false);
    this.selectedCurrency.set({ ...this.CLEARED_SELECTION });
    this.notifyCurrencyChange();
  }

  isConversionEnabled(): boolean {
    return this.conversionEnabled();
  }

  /**
   * User selects a display currency from the header menu.
   */
  setSelectedCurrency(code: string, symbol?: string): void {
    const normalized = code.trim().toUpperCase();
    const info: CurrencyInfo = {
      code: normalized,
      symbol: symbol?.trim() || this.getCurrencySymbol(normalized),
      name: normalized,
    };

    this.conversionEnabled.set(true);
    this.isConverting.set(true);
    this.fetchExchangeRate(this.baseCurrency.code, normalized).subscribe({
      next: (rate) => {
        this.applySelection(info, rate);
        this.isConverting.set(false);
      },
      error: () => {
        this.applySelection(info, 1);
        this.isConverting.set(false);
      },
    });
  }

  getBaseCurrency(): CurrencyInfo {
    return { ...this.baseCurrency };
  }

  getSelectedCurrency(): CurrencyInfo {
    return { ...this.selectedCurrency() };
  }

  /**
   * Fetch latest rate via Frankfurter (supports cross-rate when source ≠ base).
   */
  fetchExchangeRate(from: string, to: string): Observable<number> {
    const fromCode = from.trim().toUpperCase();
    const toCode = to.trim().toUpperCase();
    if (fromCode === toCode) {
      return of(1);
    }

    const cacheKey = `${fromCode}_${toCode}`;
    const cached = this.rateCache.get(cacheKey);
    if (cached != null) {
      return of(cached);
    }

    const pending = this.pendingRates.get(cacheKey);
    if (pending) {
      return pending;
    }

    const request$ = this.externalHttp
      .get<FrankfurterResponse>(
        `${this.FRANKFURTER_API}?from=${encodeURIComponent(fromCode)}&to=${encodeURIComponent(toCode)}`
      )
      .pipe(
        map((response: any) => response.rates[toCode]),
        tap((rate) => {
          this.rateCache.set(cacheKey, rate);
          this.pendingRates.delete(cacheKey);
        }),
        catchError(() => of(1)),
        shareReplay(1)
      );

    this.pendingRates.set(cacheKey, request$);
    return request$;
  }

  /**
   * Convert amount between two ISO codes using cached or fetched rates.
   */
  convertCurrency(amount: number, from: string, to: string): Observable<number> {
    const num = Number(amount);
    if (!Number.isFinite(num)) {
      return of(0);
    }
    const fromCode = from.trim().toUpperCase();
    const toCode = to.trim().toUpperCase();
    if (fromCode === toCode) {
      return of(num);
    }
    return this.fetchExchangeRate(fromCode, toCode).pipe(map((rate) => num * rate));
  }

  /** Synchronous conversion using cached rates; triggers background fetch if missing. */
  convertAmountSync(amount: string | number, fromCode?: string, toCode?: string): number {
    const num = typeof amount === 'string' ? Number.parseFloat(amount) : amount;
    if (!Number.isFinite(num)) {
      return 0;
    }

    const from = (fromCode || this.baseCurrency.code).trim().toUpperCase();
    const to = (toCode || this.selectedCurrency().code).trim().toUpperCase();
    if (!this.conversionEnabled() || from === to) {
      return num;
    }

    const cacheKey = `${from}_${to}`;
    const cached = this.rateCache.get(cacheKey);
    if (cached != null) {
      return num * cached;
    }

    this.prefetchRate(from, to);
    if (from === this.baseCurrency.code.toUpperCase() && to === this.selectedCurrency().code.toUpperCase()) {
      return num * this.exchangeRate();
    }
    return num;
  }

  formatCurrency(
    amount: string | number,
    currency?: CurrencyInfo | null,
    options: CurrencyFormatOptions = {}
  ): string {
    const {
      showSymbol = true,
      showCode = false,
      precision = 2,
      useThousandsSeparator = true,
      locale = 'en-US',
      skipConversion = false,
      notConversion = false,
      module = '',
    } = options;
    const sourceCurrency = currency || this.baseCurrency;
    let numAmount = typeof amount === 'string' ? Number.parseFloat(amount) : amount;

    if (!Number.isFinite(numAmount)) {
      return '0.00';
    }

    const isOrderListSelectCurrency =
      notConversion && module === 'order-master-list' && !this.conversionEnabled();

    if (isOrderListSelectCurrency) {
      return this.formatAmountWithCurrency(sourceCurrency, numAmount, {
        showSymbol,
        showCode,
        precision,
        useThousandsSeparator,
        locale,
      });
    }

    let displayCurrency = skipConversion
      ? sourceCurrency
      : this.selectedCurrency();

    if (!skipConversion && !this.conversionEnabled()) {
      displayCurrency = sourceCurrency;
      numAmount = typeof amount === 'string' ? Number.parseFloat(amount) : amount;
    } else if (!skipConversion) {
      numAmount = this.convertAmountSync(numAmount, sourceCurrency.code, displayCurrency.code);
    } else if (!currency) {
      displayCurrency = this.baseCurrency;
    }

    return this.formatAmountWithCurrency(displayCurrency, numAmount, {
      showSymbol,
      showCode,
      precision,
      useThousandsSeparator,
      locale,
    });
  }

  private formatAmountWithCurrency(
    displayCurrency: CurrencyInfo,
    numAmount: number,
    options: Pick<CurrencyFormatOptions, 'showSymbol' | 'showCode' | 'precision' | 'useThousandsSeparator' | 'locale'>
  ): string {
    const {
      showSymbol = true,
      showCode = false,
      precision = 2,
      useThousandsSeparator = true,
      locale = 'en-US',
    } = options;

    const symbol = displayCurrency.symbol || this.getCurrencySymbol(displayCurrency.code);

    let formattedAmount: string;
    if (useThousandsSeparator) {
      formattedAmount = new Intl.NumberFormat(locale, {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(numAmount);
    } else {
      formattedAmount = numAmount.toFixed(precision);
    }

    let result = showSymbol && symbol ? `${symbol} ${formattedAmount}` : formattedAmount;
    if (showCode && displayCurrency.code) {
      result += ` ${displayCurrency.code}`;
    }
    return result;
  }

  formatCurrencyCompact(
    amount: string | number,
    currency?: CurrencyInfo | null,
    options: CurrencyFormatOptions = {},
   
  ): string {
    return this.formatCurrency(amount, currency, {
      showSymbol: true,
      showCode: false,
      precision: 2,
      useThousandsSeparator: true,
      ...options,
      notConversion: options.notConversion || false,
    });
  }

  formatCurrencyDetailed(
    amount: string | number,
    currency?: CurrencyInfo | null,
    options: CurrencyFormatOptions = {}
  ): string {
    return this.formatCurrency(amount, currency, {
      showSymbol: true,
      showCode: true,
      precision: 2,
      useThousandsSeparator: true,
      ...options,
    });
  }

  getCurrencySymbol(currencyCode: string): string {
    const code = currencyCode?.trim().toUpperCase();
    if (!code) {
      return '';
    }
    try {
      const formatter = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      const symbolPart = formatter.formatToParts(0).find((part) => part.type === 'currency');
      return symbolPart?.value ?? code;
    } catch {
      return code;
    }
  }

  extractCurrencyFromApi(apiCurrency: any): CurrencyInfo {
    if (!apiCurrency) {
      return { ...this.baseCurrency };
    }
    return {
      symbol: apiCurrency.symbol || this.getCurrencySymbol(apiCurrency.code),
      code: (apiCurrency.code || this.baseCurrency.code).toUpperCase(),
      name: apiCurrency.name || 'Unknown Currency',
    };
  }

  parseCurrencyString(currencyString: string): number {
    if (!currencyString) return 0;
    const cleanString = currencyString.replace(/[^0-9.-]/g, '');
    const parsed = Number.parseFloat(cleanString);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  formatCurrencyForInput(amount: string | number, precision: number = 2): string {
    const numAmount = typeof amount === 'string' ? Number.parseFloat(amount) : amount;
    if (!Number.isFinite(numAmount)) {
      return '';
    }
    return numAmount.toFixed(precision);
  }

  isValidCurrencyAmount(value: string): boolean {
    if (!value || value.trim() === '') return false;
    const cleanValue = value.replace(/[^0-9.-]/g, '');
    const numValue = Number.parseFloat(cleanValue);
    return Number.isFinite(numValue) && numValue >= 0;
  }

  getDefaultCurrency(): CurrencyInfo {
    return { ...this.baseCurrency };
  }

  setDefaultCurrency(currency: CurrencyInfo): void {
    this.baseCurrency = { ...currency };
    Object.assign(this.DEFAULT_CURRENCY, currency);
  }

  private applySelection(info: CurrencyInfo, rate: number): void {
    this.selectedCurrency.set({ ...info });
    this.exchangeRate.set(rate);
    this.rateCache.set(`${this.baseCurrency.code.toUpperCase()}_${info.code.toUpperCase()}`, rate);
    this.notifyCurrencyChange();
  }

  private notifyCurrencyChange(): void {
    this.currencyVersion.update((v) => v + 1);
    this.currencyChangeSubject.next({ ...this.selectedCurrency() });
  }

  private prefetchRate(from: string, to: string): void {
    if (!this.conversionEnabled()) {
      return;
    }
    this.fetchExchangeRate(from, to).subscribe({
      next: () => this.currencyVersion.update((v) => v + 1),
    });
  }
}
